/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react"
import { supabase } from "@/lib/supabase"
import type { User, Session } from "@supabase/supabase-js"
import type { Organization } from "@/types/organization"
import i18n from "@/i18n"

type AuthContextType = {
    user: User | null
    session: Session | null
    organization: Organization | null
    organizations: Organization[]
    switchOrganization: (orgId: string) => void
    deleteOrganization: (orgId: string) => Promise<void>
    refreshOrganizations: () => Promise<void>
    userRole: 'owner' | 'admin' | 'member' | null
    loading: boolean
    signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    session: null,
    organization: null,
    organizations: [],
    switchOrganization: () => { },
    deleteOrganization: async () => { },
    refreshOrganizations: async () => { },
    userRole: null,
    loading: true,
    signOut: async () => { },
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [organization, setOrganization] = useState<Organization | null>(null)
    const [organizations, setOrganizations] = useState<Organization[]>([])
    const [userRole, setUserRole] = useState<'owner' | 'admin' | 'member' | null>(null)
    const [loading, setLoading] = useState(true)
    // Tracks whether the initial auth check has completed.
    // Prevents subsequent SIGNED_IN events (token refresh, tab focus) from
    // triggering the full-screen loading state again.
    const isInitialized = useRef(false)

    const isCreatingOrg = useRef(false)
    const createPersonalWorkspace = useCallback(async (userId: string) => {
        if (isCreatingOrg.current) return
        isCreatingOrg.current = true
        try {
            // Last minute check to ensure we don't duplicate
            const { count } = await supabase
                .from('organizations')
                .select('*', { count: 'exact', head: true })
                .eq('owner_id', userId)
                .eq('type', 'personal')

            if (count && count > 0) return

            const { data: newOrg, error: createError } = await supabase
                .from('organizations')
                .insert({
                    name: "Personal Workspace",
                    slug: `w-${userId.substring(0, 8)}-${Date.now()}`,
                    owner_id: userId,
                    type: 'personal'
                })
                .select()
                .single()

            if (createError) throw createError

            await supabase
                .from('organization_members')
                .insert({
                    organization_id: newOrg.id,
                    user_id: userId,
                    role: 'owner'
                })

            const newOrgTyped = newOrg as Organization
            setOrganizations([newOrgTyped])
            setOrganization(newOrgTyped)
            setUserRole('owner')
        } catch (e) {
            console.error("Error creating personal workspace:", e)
        } finally {
            isCreatingOrg.current = false
        }
    }, [])

    const fetchOrganization = useCallback(async (userId: string) => {
        try {
            const { data: memberData } = await supabase
                .from('organization_members')
                .select('organization_id, role, organizations(*)')
                .eq('user_id', userId)

            if (memberData && memberData.length > 0) {
                const orgs = memberData
                    .map(m => m.organizations as unknown as Organization)
                    .filter(Boolean)
                setOrganizations(orgs)

                // 1. Try to restore last active session
                const lastOrgId = localStorage.getItem(`last_org_${userId}`)
                let activeMember = memberData.find(m => m.organization_id === lastOrgId)

                // 2. Fallback: Prefer business over personal
                if (!activeMember) {
                    activeMember = memberData.find(m => (m.organizations as unknown as Organization).type === 'business')
                }

                // 3. Fallback: Use the first one (usually personal if no business)
                if (!activeMember) {
                    activeMember = memberData[0]
                }

                if (activeMember) {
                    setOrganization(activeMember.organizations as unknown as Organization)
                    setUserRole(activeMember.role as AuthContextType['userRole'])
                }
            } else {
                // Secondary check: verify if an organization was created in a parallel attempt
                const { data: secondCheck } = await supabase
                    .from('organization_members')
                    .select('organization_id')
                    .eq('user_id', userId)
                    .limit(1)

                if (!secondCheck || secondCheck.length === 0) {
                    await createPersonalWorkspace(userId)
                } else {
                    // Re-run fetch once if we detected a parallel creation
                    return fetchOrganization(userId)
                }
            }
        } catch (e) {
            console.error("Fetch org error", e)
        }
    }, [createPersonalWorkspace])

    const switchOrganization = useCallback(async (orgId: string) => {
        if (!user) return

        const targetOrg = organizations.find(o => o.id === orgId)
        if (targetOrg) {
            setOrganization(targetOrg)
            localStorage.setItem(`last_org_${user.id}`, orgId)

            const { data } = await supabase
                .from('organization_members')
                .select('role')
                .eq('organization_id', orgId)
                .eq('user_id', user.id)
                .maybeSingle()

            if (data) {
                setUserRole(data.role as AuthContextType['userRole'])
            }
        }
    }, [user, organizations])

    useEffect(() => {
        let mounted = true

        // Use a single listener to handle initial load and updates
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (!mounted) return

            setSession(session)
            setUser(session?.user ?? null)

            try {
                if (session?.user) {
                    if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
                        const needsLoadingScreen = !isInitialized.current
                        if (needsLoadingScreen) {
                            // Primera carga: mostrar pantalla completa y esperar la org
                            setLoading(true)
                            await Promise.race([
                                fetchOrganization(session.user.id),
                                new Promise<void>(resolve => setTimeout(resolve, 10_000))
                            ])
                            isInitialized.current = true
                        } else {
                            // Refresco de token / foco de tab: actualizar org en segundo plano
                            // sin mostrar la pantalla de carga al usuario
                            fetchOrganization(session.user.id)
                        }
                    }
                } else {
                    // Sign out — resetear para que el próximo login muestre loading
                    setOrganization(null)
                    setOrganizations([])
                    setUserRole(null)
                    isInitialized.current = false
                }
            } catch (e) {
                console.error('Auth state change handler error:', e)
            } finally {
                // Siempre liberar loading (no-op si ya era false)
                if (mounted) setLoading(false)
            }
        })

        return () => {
            mounted = false
            subscription.unsubscribe()
        }
    }, [fetchOrganization])

    // 3. Sincronizar idioma desde metadata de usuario
    useEffect(() => {
        if (user && user.user_metadata?.language) {
            const userLang = user.user_metadata.language
            if (userLang !== i18n.language) {
                i18n.changeLanguage(userLang)
            }
        }
    }, [user])

    const signOut = useCallback(async () => {
        // 1. Limpieza local específica
        if (user) {
            localStorage.removeItem(`last_org_${user.id}`)
        }

        // El token de Supabase suele tener un nombre dinámico basado en el proyecto
        // pero podemos buscarlo y removerlo o dejar que signOut lo maneje.
        // Por seguridad, limpiamos lo que sabemos que es de sesión:
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
                localStorage.removeItem(key)
            }
        })

        setOrganization(null)
        setOrganizations([])
        setUserRole(null)
        setSession(null)
        setUser(null)

        // 2. Redirección instantánea (Usuario percibe velocidad máxima)
        window.location.href = "/"

        // 3. Intentar notificar a Supabase en 'fire-and-forget' (si falla o tarda, no nos importa)
        try {
            await supabase.auth.signOut()
        } catch (error) {
            console.error("Error silencioso en signOut de servidor:", error)
        }
    }, [user])

    const refreshOrganizations = useCallback(async () => {
        if (user) {
            await fetchOrganization(user.id)
        }
    }, [user, fetchOrganization])

    const deleteOrganization = useCallback(async (orgId: string) => {
        if (!user) return

        const targetOrg = organizations.find(o => o.id === orgId)
        if (!targetOrg) return

        if (targetOrg.type === 'personal') {
            throw new Error('personalDeleteError')
        }

        const { error } = await supabase
            .from('organizations')
            .delete()
            .eq('id', orgId)
            .eq('owner_id', user.id) // Security check

        if (error) throw error

        // If we deleted the active organization, switch to the first available one
        if (organization?.id === orgId) {
            const nextOrg = organizations.find(o => o.id !== orgId)
            if (nextOrg) {
                await switchOrganization(nextOrg.id)
            }
        }

        await refreshOrganizations()
    }, [user, organization, organizations, switchOrganization, refreshOrganizations])

    const contextValue = useMemo(() => ({
        user,
        session,
        organization,
        organizations,
        switchOrganization,
        deleteOrganization,
        refreshOrganizations,
        userRole,
        loading,
        signOut
    }), [user, session, organization, organizations, switchOrganization, deleteOrganization, refreshOrganizations, userRole, loading, signOut])

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
